import React, { useState } from 'react';
import { MessageCircle, Phone, Mail, X, ChevronUp, ExternalLink, ShieldCheck, Car } from 'lucide-react';
import { EASY_WAY_CONTACT } from '../data/contactInfo';
import { useLanguage } from '../context/LanguageContext';

export const FloatingContactBar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { lang, easyWayContactUrl, easyWayWhatsAppUrl, t } = useLanguage();

  return (
    <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end print:hidden">
      {/* Expanded Quick Contact Panel */}
      {isOpen && (
        <div className="mb-3 w-80 max-w-[calc(100vw-2.5rem)] bg-white rounded-2xl shadow-2xl border border-[#ede6d8] p-4 text-[#172A32] animate-in fade-in slide-in-from-bottom-3 duration-200">
          <div className="flex items-center justify-between pb-3 border-b border-[#F7F3EA]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-[#073B4C] text-white flex items-center justify-center font-bold">
                <Car className="w-4 h-4" />
              </div>
              <div>
                <h4 className="font-bold text-xs text-[#172A32] leading-tight">Easy Way Rent a Car</h4>
                <p className="text-[10px] text-[#0F766E] font-semibold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0F766E] animate-pulse" />
                  {lang === 'en' ? 'Direct 24/7 Support' : lang === 'pt' ? 'Atención Inmediata 24/7' : 'Atención Inmediata 24/7'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 text-[#4A5A5F] hover:text-[#172A32] rounded-lg hover:bg-[#F7F3EA] transition-colors cursor-pointer"
              aria-label={lang === 'en' ? 'Close contact panel' : lang === 'pt' ? 'Cerrar panel de contacto' : 'Cerrar panel de contacto'}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="text-xs text-[#4A5A5F] mt-2 mb-3 leading-relaxed">
            <strong>{lang === 'en' ? 'Your ideal vehicle in Cancun:' : lang === 'pt' ? 'Tu vehículo ideal en Cancún:' : 'Tu vehículo ideal en Cancún:'}</strong> {lang === 'en' ? 'Direct assistance, unlimited mileage, and honest transparent pricing without hidden fees:' : lang === 'pt' ? 'Asesoría directa, kilometraje libre y cotizaciones transparentes sin cobros ocultos:' : 'Asesoría directa, kilometraje libre y cotizaciones transparentes sin cobros ocultos:'}
          </p>

          <div className="space-y-2">
            {/* WhatsApp */}
            <a
              href={easyWayWhatsAppUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-2.5 rounded-xl bg-[#0F766E]/5 hover:bg-[#0F766E]/10 text-[#073B4C] border border-[#0F766E]/20 transition-colors group"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-[#0F766E] text-white flex items-center justify-center">
                  <MessageCircle className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <p className="text-[11px] font-bold">{lang === 'en' ? 'Direct WhatsApp' : lang === 'pt' ? 'WhatsApp Directo' : 'WhatsApp Directo'}</p>
                  <p className="text-[10px] text-[#073B4C]/70">{EASY_WAY_CONTACT.whatsapp.display}</p>
                </div>
              </div>
              <span className="text-[10px] uppercase font-extrabold text-[#073B4C] bg-white px-2 py-0.5 rounded-md shadow-2xs group-hover:bg-[#0F766E] group-hover:text-white transition-colors">
                {lang === 'en' ? 'Chat' : lang === 'pt' ? 'Chatear' : 'Chatear'}
              </span>
            </a>

            {/* Mexico Local Phone */}
            <a
              href={EASY_WAY_CONTACT.phoneMexico.link}
              className="flex items-center justify-between p-2.5 rounded-xl bg-[#F7F3EA] hover:bg-[#ede6d8] text-[#172A32] border border-[#ede6d8] transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-[#172A32] text-white flex items-center justify-center">
                  <Phone className="w-3.5 h-3.5" />
                </div>
                <div className="text-left">
                  <p className="text-[11px] font-bold">{lang === 'en' ? 'Mexico / Cancun Line' : lang === 'pt' ? 'Línea México / Cancún' : 'Línea México / Cancún'}</p>
                  <p className="text-[10px] text-[#4A5A5F]">{EASY_WAY_CONTACT.phoneMexico.display}</p>
                </div>
              </div>
              <span className="text-[10px] font-bold text-[#172A32] bg-white px-2 py-0.5 rounded-md border border-[#ede6d8]">
                {lang === 'en' ? 'Call' : lang === 'pt' ? 'Llamar' : 'Llamar'}
              </span>
            </a>

            {/* USA Toll Free */}
            <a
              href={EASY_WAY_CONTACT.phoneUsa.link}
              className="flex items-center justify-between p-2.5 rounded-xl bg-[#F7F3EA] hover:bg-[#ede6d8] text-[#172A32] border border-[#ede6d8] transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-[#073B4C] text-white flex items-center justify-center">
                  <Phone className="w-3.5 h-3.5" />
                </div>
                <div className="text-left">
                  <p className="text-[11px] font-bold">{lang === 'en' ? 'USA & Canada (Toll Free)' : lang === 'pt' ? 'Desde EE.UU. & Canadá (Toll Free)' : 'Desde EE.UU. & Canadá (Toll Free)'}</p>
                  <p className="text-[10px] text-[#4A5A5F]">{EASY_WAY_CONTACT.phoneUsa.display}</p>
                </div>
              </div>
              <span className="text-[10px] font-bold text-[#172A32] bg-white px-2 py-0.5 rounded-md border border-[#ede6d8]">
                {lang === 'en' ? 'Call' : lang === 'pt' ? 'Llamar' : 'Llamar'}
              </span>
            </a>

            {/* Email */}
            <a
              href={EASY_WAY_CONTACT.email.link}
              className="flex items-center justify-between p-2.5 rounded-xl bg-[#F7F3EA] hover:bg-[#ede6d8] text-[#172A32] border border-[#ede6d8] transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-[#A8471A] text-white flex items-center justify-center">
                  <Mail className="w-3.5 h-3.5" />
                </div>
                <div className="text-left">
                  <p className="text-[11px] font-bold">{lang === 'en' ? 'Email' : lang === 'pt' ? 'Correo Electrónico' : 'Correo Electrónico'}</p>
                  <p className="text-[10px] text-[#4A5A5F]">{EASY_WAY_CONTACT.email.address}</p>
                </div>
              </div>
              <span className="text-[10px] font-bold text-[#172A32] bg-white px-2 py-0.5 rounded-md border border-[#ede6d8]">
                {lang === 'en' ? 'Write' : lang === 'pt' ? 'Escribir' : 'Escribir'}
              </span>
            </a>
          </div>

          <div className="mt-3 pt-2.5 border-t border-[#ede6d8] flex items-center justify-between text-[11px]">
            <span className="text-[#4A5A5F] flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-[#0F766E]" />
              {lang === 'pt' ? 'Seguro TPL incluído' : lang === 'en' ? 'TPL insurance included' : 'Seguro TPL incluido'}
            </span>
            <a
              href={easyWayContactUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#073B4C] font-bold hover:underline inline-flex items-center gap-1"
            >
              {lang === 'en' ? 'Contact & Support' : lang === 'pt' ? 'Contato e Atendimento' : 'Atención y Sucursales'} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}

      {/* Main Floating Buttons Trigger */}
      <div className="flex items-center gap-2 shadow-2xl">
        {/* Direct WhatsApp Action Pill */}
        <a
          href={easyWayWhatsAppUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-[#0F766E] via-[#0F766E] to-[#073B4C] hover:from-[#0d695d] hover:via-[#0d695d] hover:to-[#052a37] text-white font-bold text-xs uppercase tracking-wider shadow-lg shadow-[#0F766E]/30 transition-all transform hover:scale-105 active:scale-95"
          title={lang === 'en' ? 'Contact Easy Way Rent a Car via WhatsApp' : lang === 'pt' ? 'Contactar a Easy Way Rent a Car por WhatsApp' : 'Contactar a Easy Way Rent a Car por WhatsApp'}
        >
          <MessageCircle className="w-4 h-4 fill-current" />
          <span className="hidden sm:inline">{lang === 'en' ? 'WhatsApp Easy Way' : lang === 'pt' ? 'WhatsApp Easy Way' : 'WhatsApp Easy Way'}</span>
          <span className="sm:hidden">WhatsApp</span>
        </a>

        {/* Toggle Other Contact Options */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`p-3 rounded-full text-white font-bold transition-all shadow-lg cursor-pointer ${
            isOpen ? 'bg-[#172A32] hover:bg-[#073B4C]' : 'bg-[#073B4C] hover:bg-[#172A32]'
          }`}
          title={lang === 'en' ? 'View all phone and contact options' : lang === 'pt' ? 'Ver todos los teléfonos y formas de contacto' : 'Ver todos los teléfonos y formas de contacto'}
          aria-label={lang === 'en' ? 'Open contact options' : lang === 'pt' ? 'Abrir opciones de contacto' : 'Abrir opciones de contacto'}
        >
          {isOpen ? <X className="w-5 h-5" /> : <Phone className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};
